import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext.jsx';
import { loadWithRetry } from '../lib/loadWithRetry.js';
import { fetchPublishedEngagements, groupByCategory } from '../lib/broaderEngagements.js';
import EngagementCard from '../components/broaderEngagements/EngagementCard.jsx';
import EngagementDetailModal from '../components/broaderEngagements/EngagementDetailModal.jsx';

// Community module reached from the Explore grid (broader_engagements table,
// migration 021). The editorial framing stays fixed; the entries under each
// (sub)category are submitted by participants and approved by admins, like
// every other Explore tile.
export default function BroaderEngagements() {
  const { t } = useTranslation();
  const { isActive } = useAuth();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [detail, setDetail] = useState(null);
  const [searchParams] = useSearchParams();
  const prompts = t('broaderEngagements.theory.horizon.prompts', { returnObjects: true });

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    loadWithRetry(fetchPublishedEngagements, { label: 'broader-engagements' })
      .then((rows) => !cancelled && setItems(rows))
      .catch((err) => !cancelled && setError(err.message ?? String(err)));
    return () => {
      cancelled = true;
    };
  }, [retryKey]);

  // Deep link from a What's New notice: /broader-engagements?entry=<id>.
  useEffect(() => {
    const id = searchParams.get('entry');
    if (!id || !items) return;
    const match = items.find((m) => m.id === id);
    if (match) setDetail(match);
  }, [items, searchParams]);

  const groups = useMemo(() => (items ? groupByCategory(items) : null), [items]);

  return (
    <article className="mx-auto max-w-3xl px-6 pb-20 pt-28">
      <header className="mb-12 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="section-label">{t('broaderEngagements.label')}</div>
          <h1 className="font-serif text-4xl font-semibold text-cream">
            {t('broaderEngagements.heading')}
          </h1>
          <p className="mt-4 text-base leading-relaxed text-ink-primary">
            {t('broaderEngagements.intro')}
          </p>
        </div>
        {isActive && (
          <Link to="/broader-engagements/new" className="btn-primary">
            {t('broaderEngagements.add')}
          </Link>
        )}
      </header>

      {error && (
        <div className="mb-10 rounded-lg border border-red-500/40 bg-red-950/30 p-6 text-center">
          <p className="text-sm text-red-200">{error}</p>
          <button
            type="button"
            className="btn-primary mt-4"
            onClick={() => setRetryKey((k) => k + 1)}
          >
            {t('common.tryAgain')}
          </button>
        </div>
      )}

      {/* ── 1. Expanding the confines of hermeneutics as a theory ── */}
      <section className="mb-14">
        <h2 className="font-serif text-2xl font-semibold text-gold-light">
          {t('broaderEngagements.theory.heading')}
        </h2>
        <p className="mt-3 text-base leading-relaxed text-ink-primary">
          {t('broaderEngagements.theory.intro')}
        </p>

        <div className="mt-8 space-y-6">
          <Subsection heading={t('broaderEngagements.theory.horizon.heading')}>
            <p className="text-sm leading-relaxed text-ink-secondary">
              {t('broaderEngagements.theory.horizon.body')}
            </p>
            <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm text-ink-primary">
              {Array.isArray(prompts) && prompts.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <Entries items={groups?.theory_horizon} loading={!items && !error} onOpen={setDetail} />
          </Subsection>

          <Subsection heading={t('broaderEngagements.theory.implicit.heading')}>
            <p className="text-sm leading-relaxed text-ink-secondary">
              {t('broaderEngagements.theory.implicit.body')}
            </p>
            <Entries items={groups?.theory_implicit} loading={!items && !error} onOpen={setDetail} />
          </Subsection>
        </div>
      </section>

      {/* ── 2. Practical relevance beyond hermeneutics' own arena ── */}
      <section className="mb-14">
        <h2 className="font-serif text-2xl font-semibold text-gold-light">
          {t('broaderEngagements.practice.heading')}
        </h2>
        <p className="mt-3 text-base leading-relaxed text-ink-primary">
          {t('broaderEngagements.practice.intro')}
        </p>
        <div className="mt-8">
          <Subsection heading={t('broaderEngagements.practice.subheading')}>
            <p className="text-sm leading-relaxed text-ink-secondary">
              {t('broaderEngagements.practice.body')}
            </p>
            <Entries items={groups?.practice} loading={!items && !error} onOpen={setDetail} />
          </Subsection>
        </div>
      </section>

      <div className="cta-box">
        <h2 className="cta-title">{t('broaderEngagements.contribute.heading')}</h2>
        <p className="cta-body">{t('broaderEngagements.contribute.body')}</p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          {isActive && (
            <Link to="/broader-engagements/new" className="btn-primary">
              {t('broaderEngagements.add')}
            </Link>
          )}
          <Link to="/teaching/new" className={isActive ? 'btn-ghost' : 'btn-primary'}>
            {t('broaderEngagements.contribute.postTeachingCta')}
          </Link>
          <Link to="/research-tools/new" className={isActive ? 'btn-ghost' : 'btn-primary'}>
            {t('broaderEngagements.contribute.postToolCta')}
          </Link>
          <Link to="/whats-new/new" className={isActive ? 'btn-ghost' : 'btn-primary'}>
            {t('broaderEngagements.contribute.postCta')}
          </Link>
          <Link to="/about" className="btn-ghost">
            {t('broaderEngagements.contribute.writeCta')}
          </Link>
        </div>
      </div>

      <EngagementDetailModal
        item={detail}
        onClose={() => setDetail(null)}
        onDeleted={(id) => setItems((prev) => prev?.filter((m) => m.id !== id) ?? prev)}
      />
    </article>
  );
}

function Subsection({ heading, children }) {
  return (
    <div className="rounded-md border border-white/10 bg-navy-base/40 p-5">
      <h3 className="font-serif text-xl font-semibold text-cream">{heading}</h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

// The community entries under one (sub)category.
function Entries({ items, loading, onOpen }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <p role="status" aria-live="polite" className="mt-5 text-sm text-ink-muted">
        {t('common.loading')}
      </p>
    );
  }
  if (!items) return null;
  return (
    <div className="mt-5 border-t border-white/10 pt-5">
      <div className="mb-3 text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-gold">
        {t('broaderEngagements.entriesHeading', { count: items.length })}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('broaderEngagements.emptyCategory')}</p>
      ) : (
        <div className="grid gap-3">
          {items.map((item) => (
            <EngagementCard key={item.id} item={item} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}
